import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, type ZodTypeAny } from 'zod';
import { cn } from '@/lib/utils';

export type CampoTipo = 'text' | 'number' | 'select' | 'checkbox';

export interface OpcionCampo {
  value: string;
  label: string;
}

export interface CampoExploracionDefinicion {
  name: string;
  type: CampoTipo;
  label: string;
  required?: boolean;
  options?: OpcionCampo[];
  min?: number;
  max?: number;
}

export interface DynamicFormAction {
  key: string;
  label: string;
  onClick: (values: Record<string, unknown>) => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  /** false = guarda los valores tal cual (borrador), sin bloquear por campos incompletos. Default: true. */
  requiresValidation?: boolean;
}

/** Construye un z.object() al vuelo a partir del esquemaJson de la plantilla. */
function buildZodSchema(esquemaJson: CampoExploracionDefinicion[]) {
  const shape: Record<string, ZodTypeAny> = {};

  for (const campo of esquemaJson) {
    let schema: ZodTypeAny;

    if (campo.type === 'number') {
      let inner = z.number({ invalid_type_error: `${campo.label} debe ser un número` });
      if (campo.min !== undefined) inner = inner.min(campo.min, `${campo.label} debe ser mayor o igual a ${campo.min}`);
      if (campo.max !== undefined) inner = inner.max(campo.max, `${campo.label} debe ser menor o igual a ${campo.max}`);
      const numOrOptional: ZodTypeAny = campo.required ? inner : inner.optional();
      // Los inputs HTML siempre entregan string; convertimos "" -> undefined antes de castear a número.
      schema = z.preprocess(
        (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
        numOrOptional,
      );
    } else if (campo.type === 'checkbox') {
      schema = campo.required
        ? z.boolean().refine((v) => v === true, { message: `Debes confirmar "${campo.label}"` })
        : z.boolean().optional();
    } else {
      // 'text' y 'select' comparten la base string
      let strSchema = z.string();
      if (campo.min !== undefined) strSchema = strSchema.min(campo.min, `${campo.label} debe tener al menos ${campo.min} caracteres`);
      if (campo.max !== undefined) strSchema = strSchema.max(campo.max, `${campo.label} debe tener máximo ${campo.max} caracteres`);

      let withOptions: ZodTypeAny = strSchema;
      if (campo.type === 'select' && campo.options?.length) {
        const validValues = campo.options.map((o) => o.value);
        withOptions = strSchema.refine((v) => v === '' || validValues.includes(v), { message: 'Selecciona una opción válida' });
      }

      schema = campo.required
        ? (campo.type === 'select' ? withOptions.refine((v) => v !== '', { message: 'Requerido' }) : strSchema.min(1, 'Requerido'))
        : withOptions.optional();
    }

    shape[campo.name] = schema;
  }

  return z.object(shape);
}

function buildDefaultValues(esquemaJson: CampoExploracionDefinicion[], initialValues?: Record<string, unknown>) {
  const defaults: Record<string, unknown> = {};
  for (const campo of esquemaJson) {
    if (initialValues && campo.name in initialValues && initialValues[campo.name] !== null) {
      defaults[campo.name] = campo.type === 'number' || campo.type === 'select' ? String(initialValues[campo.name]) : initialValues[campo.name];
      continue;
    }
    defaults[campo.name] = campo.type === 'checkbox' ? false : '';
  }
  return defaults;
}

/** Convierte los valores crudos del form (todo string/boolean) a los tipos reales del datosJson. */
export function coerceFormValues(esquemaJson: CampoExploracionDefinicion[], raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const campo of esquemaJson) {
    const v = raw[campo.name];
    if (campo.type === 'number') {
      out[campo.name] = v === '' || v === undefined || v === null ? null : Number(v);
    } else if (campo.type === 'checkbox') {
      out[campo.name] = !!v;
    } else {
      out[campo.name] = v ?? '';
    }
  }
  return out;
}

interface DynamicFormRendererProps {
  esquemaJson: CampoExploracionDefinicion[];
  initialValues?: Record<string, unknown>;
  disabled?: boolean;
  actions?: DynamicFormAction[];
}

export function DynamicFormRenderer({ esquemaJson, initialValues, disabled = false, actions = [] }: DynamicFormRendererProps) {
  const schema = useMemo(() => buildZodSchema(esquemaJson), [esquemaJson]);
  const defaultValues = useMemo(() => buildDefaultValues(esquemaJson, initialValues), [esquemaJson, initialValues]);

  const {
    register,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<Record<string, unknown>>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  async function runAction(action: DynamicFormAction) {
    if (action.requiresValidation === false) {
      await trigger();
      action.onClick(coerceFormValues(esquemaJson, getValues()));
      return;
    }
    handleSubmit((values) => action.onClick(coerceFormValues(esquemaJson, values)))();
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50 disabled:text-text-secondary';

  return (
    <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
      {esquemaJson.map((campo) => {
        const error = errors[campo.name];
        return (
          <div key={campo.name}>
            {campo.type === 'checkbox' ? (
              <div className="flex items-center gap-2">
                <input
                  id={campo.name}
                  type="checkbox"
                  disabled={disabled}
                  {...register(campo.name)}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-300"
                />
                <label htmlFor={campo.name} className="text-sm text-text-primary">
                  {campo.label} {campo.required && <span className="text-red-500">*</span>}
                </label>
              </div>
            ) : (
              <>
                <label htmlFor={campo.name} className="block text-xs font-medium text-text-secondary mb-1">
                  {campo.label} {campo.required && <span className="text-red-500">*</span>}
                </label>

                {campo.type === 'text' && (
                  <input id={campo.name} type="text" disabled={disabled} {...register(campo.name)} className={inputCls} />
                )}

                {campo.type === 'number' && (
                  <input id={campo.name} type="number" disabled={disabled} {...register(campo.name)} className={inputCls} />
                )}

                {campo.type === 'select' && (
                  <select id={campo.name} disabled={disabled} {...register(campo.name)} className={cn(inputCls, 'bg-white')}>
                    <option value="">Selecciona...</option>
                    {campo.options?.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}

            {error && <p className="text-xs text-red-500 mt-1">{String(error.message ?? 'Campo inválido')}</p>}
          </div>
        );
      })}

      {!disabled && actions.length > 0 && (
        <div className="flex items-center gap-2 pt-1">
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              disabled={action.disabled}
              onClick={() => runAction(action)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50',
                action.variant === 'secondary'
                  ? 'bg-white border border-gray-200 text-text-primary hover:bg-gray-50'
                  : 'bg-primary-600 text-white hover:bg-primary-700',
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
